// src/components/InvoiceDetails.js

import React from "react";
import PropTypes from "prop-types";
import { Box, Grid, Typography, TextField, MenuItem } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import MDButton from "components/MDButton";
import { useLanguage } from "context/LanguageContext";
import Loader from "components/Loader/Loader";

function InvoiceDetails({ documentData, setDocumentData, handleDetailsSave }) {
  const { t } = useLanguage();
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));

  if (!documentData || !documentData.data) {
    return <Loader message={t("loading")} />;
  }

  const data = documentData.data;

  const getTranslation = (key, fallback) => {
    const translated = t(key);
    return translated && translated !== key ? translated : fallback;
  };

  const documentTypeLabel = getTranslation("documentType", "Dokumento tipas");

  const documentTypeOptions = [
    { value: "purchase", labelKey: "purchaseDocument", fallback: "Pirkimo dokumentas" },
    { value: "sale", labelKey: "salesDocument", fallback: "Pardavimo dokumentas" },
    { value: "return", labelKey: "returnDocument", fallback: "Grąžinimo dokumentas" },
    { value: "other", labelKey: "otherDocument", fallback: "Kitas dokumentas" },
  ];

  const normalizeDocumentType = (raw) => {
    if (typeof raw !== "string") return "";
    const norm = raw.trim().toLowerCase();
    if (["purchase", "sale", "return", "credit", "other"].includes(norm)) {
      return norm === "credit" ? "return" : norm;
    }
    return "";
  };

  const currentDocumentType = (() => {
    if (data.is_purchase) return "purchase";
    if (data.is_sale) return "sale";
    if (data.is_return) return "return";
    if (data.is_other) return "other";
    return normalizeDocumentType(data.document_type);
  })();

  const parseDateValue = (value) => {
    if (!value) return null;

    const normalized = String(value).trim();
    const isoMatch = normalized.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/);
    const ltMatch = normalized.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})/);
    const match = isoMatch || ltMatch;

    if (!match) return null;

    const year = Number(isoMatch ? match[1] : match[3]);
    const month = Number(match[2]);
    const day = Number(isoMatch ? match[3] : match[1]);
    const parsed = new Date(year, month - 1, day);

    if (
      parsed.getFullYear() !== year ||
      parsed.getMonth() !== month - 1 ||
      parsed.getDate() !== day
    ) {
      return null;
    }

    return parsed;
  };

  const paymentTermDays = (() => {
    const invoiceDate = parseDateValue(data.invoice_date);
    const dueDate = parseDateValue(data.due_date);
    if (!invoiceDate || !dueDate) return "";

    const invoiceTime = new Date(
      invoiceDate.getFullYear(),
      invoiceDate.getMonth(),
      invoiceDate.getDate()
    ).getTime();
    const dueTime = new Date(
      dueDate.getFullYear(),
      dueDate.getMonth(),
      dueDate.getDate()
    ).getTime();

    return Math.round((dueTime - invoiceTime) / (24 * 60 * 60 * 1000));
  })();

  const handleDocumentTypeChange = (event) => {
    const { value } = event.target;
    const normalizedValue = normalizeDocumentType(value);
    const updatedFlags = {
      is_purchase: normalizedValue === "purchase",
      is_sale: normalizedValue === "sale",
      is_return: normalizedValue === "return",
      is_other: normalizedValue === "other",
    };

    setDocumentData({
      ...documentData,
      data: {
        ...data,
        ...updatedFlags,
        document_type: normalizedValue || null,
      },
    });
  };

  return (
    <Box sx={{ padding: 1 }}>
      {/* Invoice Details Section */}
      <Grid container spacing={1}>
        {/* Document Type (read-only) */}
        <Grid item xs={12} md={6}>
          <TextField
            select
            label={documentTypeLabel}
            value={currentDocumentType}
            onChange={handleDocumentTypeChange}
            fullWidth
            margin="dense"
            size="small"
            SelectProps={{
              displayEmpty: true,
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: 40,
              },
              "& .MuiSelect-select": {
                display: "flex",
                alignItems: "center",
                padding: "8px 14px",
                height: "1.4375em",
                minHeight: "unset",
              },
            }}
          >
            <MenuItem value="">{getTranslation("noDocumentType", "—")}</MenuItem>
            {documentTypeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {getTranslation(option.labelKey, option.fallback)}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        {/* Invoice Date */}
        <Grid item xs={12} md={6}>
          <TextField
            label={t("invoiceDate")}
            value={data.invoice_date || ""}
            onChange={(e) =>
              setDocumentData({
                ...documentData,
                data: { ...data, invoice_date: e.target.value },
              })
            }
            fullWidth
            margin="dense"
            size="small"
          />
        </Grid>
        {/* Invoice ID */}
        <Grid item xs={12} md={6}>
          <TextField
            label={t("invoiceId")}
            value={data.invoice_id || ""}
            onChange={(e) =>
              setDocumentData({
                ...documentData,
                data: { ...data, invoice_id: e.target.value },
              })
            }
            fullWidth
            margin="dense"
            size="small"
          />
        </Grid>
        {/* Total Amount */}
        <Grid item xs={12} md={6}>
          <TextField
            label={t("totalAmount")}
            value={data.total_amount || ""}
            onChange={(e) =>
              setDocumentData({
                ...documentData,
                data: { ...data, total_amount: e.target.value },
              })
            }
            fullWidth
            margin="dense"
            size="small"
          />
        </Grid>
        {/* Total Tax Amount */}
        {
          <Grid item xs={12} md={6}>
            <TextField
              label={t("totalTaxAmount")}
              value={data.total_tax_amount || ""}
              onChange={(e) =>
                setDocumentData({
                  ...documentData,
                  data: { ...data, total_tax_amount: e.target.value },
                })
              }
              fullWidth
              margin="dense"
              size="small"
            />
          </Grid>
        }
        {/* Net Amount */}
        {
          <Grid item xs={12} md={6}>
            <TextField
              label={t("netAmount")}
              value={data.net_amount || ""}
              onChange={(e) =>
                setDocumentData({
                  ...documentData,
                  data: { ...data, net_amount: e.target.value },
                })
              }
              fullWidth
              margin="dense"
              size="small"
            />
          </Grid>
        }
        {/* VAT Percentage */}
        <Grid item xs={12} md={6}>
          <TextField
            label={t("vatPercentage")}
            value={data.vat_percentage || ""}
            onChange={(e) =>
              setDocumentData({
                ...documentData,
                data: { ...data, vat_percentage: e.target.value },
              })
            }
            fullWidth
            margin="dense"
            size="small"
          />
        </Grid>
        {/* Currency */}
        {
          <Grid item xs={12} md={6}>
            <TextField
              label={t("currency")}
              value={data.currency || ""}
              onChange={(e) =>
                setDocumentData({
                  ...documentData,
                  data: { ...data, currency: e.target.value },
                })
              }
              fullWidth
              margin="dense"
              size="small"
            />
          </Grid>
        }
        {/* Due Date */}
        {
          <Grid item xs={12} md={6}>
            <TextField
              label={t("dueDate")}
              value={data.due_date || ""}
              onChange={(e) =>
                setDocumentData({
                  ...documentData,
                  data: { ...data, due_date: e.target.value },
                })
              }
              fullWidth
              margin="dense"
              size="small"
            />
          </Grid>
        }
        <Grid item xs={12} md={6}>
          <TextField
            label={t("paymentTermDays")}
            value={paymentTermDays}
            fullWidth
            margin="dense"
            size="small"
            InputProps={{ readOnly: true }}
          />
        </Grid>
      </Grid>

      {/* Supplier Information Section */}
      <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
        {t("supplierInformation")}
      </Typography>
      <Grid container spacing={1}>
        {/* Supplier Name */}
        <Grid item xs={12} md={6}>
          <TextField
            label={t("supplierName")}
            value={data.supplier_name || ""}
            onChange={(e) =>
              setDocumentData({
                ...documentData,
                data: { ...data, supplier_name: e.target.value },
              })
            }
            fullWidth
            margin="dense"
            size="small"
          />
        </Grid>
        {/* Supplier Tax ID */}
        <Grid item xs={12} md={6}>
          <TextField
            label={t("supplierTaxId")}
            value={data.supplier_tax_id || ""}
            onChange={(e) =>
              setDocumentData({
                ...documentData,
                data: { ...data, supplier_tax_id: e.target.value },
              })
            }
            fullWidth
            margin="dense"
            size="small"
          />
        </Grid>
        {/* Supplier IBAN */}
        {
          <Grid item xs={12} md={6}>
            <TextField
              label={t("supplierIban")}
              value={data.supplier_iban || ""}
              onChange={(e) =>
                setDocumentData({
                  ...documentData,
                  data: { ...data, supplier_iban: e.target.value },
                })
              }
              fullWidth
              margin="dense"
              size="small"
            />
          </Grid>
        }
        {/* Supplier Address */}
        {
          <Grid item xs={12} md={6}>
            <TextField
              label={t("supplierAddress")}
              value={data.supplier_address || ""}
              onChange={(e) =>
                setDocumentData({
                  ...documentData,
                  data: { ...data, supplier_address: e.target.value },
                })
              }
              fullWidth
              margin="dense"
              size="small"
            />
          </Grid>
        }
        {/* Supplier Registration */}
        <Grid item xs={12} md={6}>
          <TextField
            label={t("supplierRegistration")}
            value={data.supplier_registration || ""}
            onChange={(e) =>
              setDocumentData({
                ...documentData,
                data: { ...data, supplier_registration: e.target.value },
              })
            }
            fullWidth
            margin="dense"
            size="small"
          />
        </Grid>
      </Grid>

      {/* Receiver Information Section */}
      <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
        {t("receiverInformation")}
      </Typography>
      <Grid container spacing={1}>
        {/* Receiver Name */}
        <Grid item xs={12} md={6}>
          <TextField
            label={t("receiverName")}
            value={data.receiver_name || ""}
            onChange={(e) =>
              setDocumentData({
                ...documentData,
                data: { ...data, receiver_name: e.target.value },
              })
            }
            fullWidth
            margin="dense"
            size="small"
          />
        </Grid>
        {/* Receiver Tax ID */}
        <Grid item xs={12} md={6}>
          <TextField
            label={t("receiverTaxId")}
            value={data.receiver_tax_id || ""}
            onChange={(e) =>
              setDocumentData({
                ...documentData,
                data: { ...data, receiver_tax_id: e.target.value },
              })
            }
            fullWidth
            margin="dense"
            size="small"
          />
        </Grid>
        {/* Receiver Registration */}
        <Grid item xs={12} md={6}>
          <TextField
            label={t("receiverRegistration")}
            value={data.receiver_registration || ""}
            onChange={(e) =>
              setDocumentData({
                ...documentData,
                data: { ...data, receiver_registration: e.target.value },
              })
            }
            fullWidth
            margin="dense"
            size="small"
          />
        </Grid>
      </Grid>

      {/* Save Button */}
      <Box sx={{ mt: 3 }}>
        <MDButton variant="contained" color="info" onClick={handleDetailsSave}>
          {t("saveChanges")}
        </MDButton>
      </Box>
    </Box>
  );
}

InvoiceDetails.propTypes = {
  documentData: PropTypes.shape({
    data: PropTypes.shape({
      invoice_date: PropTypes.string,
      invoice_id: PropTypes.string,
      total_amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      total_tax_amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      net_amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      vat_percentage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      currency: PropTypes.string,
      due_date: PropTypes.string,
      supplier_name: PropTypes.string,
      supplier_tax_id: PropTypes.string,
      supplier_iban: PropTypes.string,
      supplier_address: PropTypes.string,
      supplier_registration: PropTypes.string,
      receiver_name: PropTypes.string,
      receiver_tax_id: PropTypes.string,
      receiver_registration: PropTypes.string,
      notes: PropTypes.string,
      // Added document type flags
      is_purchase: PropTypes.bool,
      is_sale: PropTypes.bool,
      is_return: PropTypes.bool,
      is_other: PropTypes.bool,
      document_type: PropTypes.string,
    }),
  }).isRequired,
  setDocumentData: PropTypes.func.isRequired,
  handleDetailsSave: PropTypes.func.isRequired,
};

export default InvoiceDetails;
